import "./Chat.css";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Components, createStore } from "botframework-webchat";
import { FluentThemeProvider } from "botframework-webchat-fluent-theme";
import { CopilotStudioClient } from "@microsoft/agents-copilotstudio-client";
import { settings, loginRequest } from "../settings";
import { useMsal, useMsalAuthentication } from "@azure/msal-react";
import { InteractionType, InteractionRequiredAuthError } from "@azure/msal-browser";
import { isInIframe } from "../components/iframe";
import { hooks } from "botframework-webchat";
import { BehaviorSubject, Observable, ReplaySubject } from "rxjs";

const { usePostActivity } = hooks;
const { BasicWebChat, Composer } = Components;

/** Allowed MIME types for file uploads. */
const ALLOWED_TYPES = [
  "image/png",
  "image/jpeg",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/csv",
];
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024; // 5 MB
const UPLOAD_ACCEPT = "image/png,image/jpeg,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv";

/** MSAL scope required to invoke Copilot Studio agents. */
const COPILOT_SCOPES = ["https://api.powerplatform.com/CopilotStudio.Copilots.Invoke"];

/** Use popup inside iframes to avoid redirect navigation breaking the parent frame. */
const interactionType = isInIframe() ? InteractionType.Popup : InteractionType.Redirect;

/**
 * CopilotChat — generic Microsoft Copilot Studio chat interface.
 *
 * Renders the Bot Framework WebChat UI for any registered Copilot Studio agent.
 * Agent-specific configuration (connection settings, bot avatar, quick-question shortcuts)
 * is resolved from settings.js via the agentKey prop, so three independent routes
 * can each mount this component with a different identity without duplicating code.
 *
 * @param {string}   agentKey       - Key into settings object ('copilot' | 'claude' | 'jira')
 * @param {object}   msalUser       - Authenticated user profile fetched from Microsoft Graph
 * @param {Function} onSendboxClick - Callback invoked after a message is sent; scrolls the chat container
 */
function CopilotChat({ agentKey, botConfig, msalUser, onSendboxClick, theme = "light" }) {
  const agentConfig = (() => {
    if (botConfig) {
      let qq = [];
      try {
        const raw = botConfig.QuickQuestions;
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (Array.isArray(parsed)) qq = parsed;
      } catch {}
      return {
        directConnectUrl: botConfig.DirectConnectUrl,
        environmentId:    botConfig.EnvironmentId,
        tenantId:         botConfig.TenantId,
        appClientId:      botConfig.AppClientId,
        agentIdentifier:  botConfig.AgentIdentifier,
        botAvatar:        botConfig.NavIcon || botConfig.BotAvatar || "/images/robot.png",
        quickQuestions:   qq,
        allowFileUpload:  !!botConfig.AllowFileUpload,
      };
    }
    return settings[agentKey];
  })();

  const initializedRef = useRef(false);
  const { instance, accounts, inProgress } = useMsal();
  const lastConsentActivityIdRef = useRef(null);
  const [deletedActivityIds, setDeletedActivityIds] = useState(() => new Set());
  const [approvedConsentActivityIds, setApprovedConsentActivityIds] = useState(() => new Set());
  const [connection, setConnection] = useState(null);
  const [showPopupBlocked, setShowPopupBlocked] = useState(false);
  const [stagedFiles, setStagedFiles] = useState([]);
  const stagedFilesSetterRef = useRef(null);
  stagedFilesSetterRef.current = setStagedFiles;
  const stagedFilesRef = useRef([]);
  stagedFilesRef.current = stagedFiles;
  // Object URLs for image previews (null for non-image files)
  const [stagedPreviews, setStagedPreviews] = useState([]);
  // Always holds the latest onSendboxClick so the store middleware can call it
  // without re-creating the store when the prop reference changes.
  const onSendboxClickRef = useRef(onSendboxClick);
  onSendboxClickRef.current = onSendboxClick;

  const [isBotTyping, setIsBotTyping] = useState(false);
  const setBotTypingRef = useRef(setIsBotTyping);
  setBotTypingRef.current = setIsBotTyping;
  const [showSlowMsg, setShowSlowMsg] = useState(false);
  const slowTimerRef = useRef(null);
  const [loaderPortalEl, setLoaderPortalEl] = useState(null);
  const loaderPortalRef = useRef(null);

  console.log("MSAL User in Chat:", msalUser);

  /** Scroll to bottom when user clicks in the sendbox area. */
  useEffect(() => {
    console.log("useEffect on Send box click Invoked");
    const handleClick = (event) => {
      if (event.target.closest(".sendbox") && onSendboxClick) {
        setTimeout(() => onSendboxClick(), 30);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [onSendboxClick]);

  /** Block drag-and-drop when the agent does not allow file uploads. */
  useEffect(() => {
    if (agentConfig?.allowFileUpload) return;
    const prevent = (e) => { e.preventDefault(); e.stopPropagation(); };
    document.addEventListener("dragenter", prevent, true);
    document.addEventListener("dragover", prevent, true);
    document.addEventListener("drop", prevent, true);
    return () => {
      document.removeEventListener("dragenter", prevent, true);
      document.removeEventListener("dragover", prevent, true);
      document.removeEventListener("drop", prevent, true);
    };
  }, [agentConfig?.allowFileUpload]);

  /** Keep the sendbox placeholder text in sync (WebChat resets it on re-render). */
  useEffect(() => {
    const updateSendBoxText = () => {
      const textarea = document.querySelector('textarea[data-testid="send box text area"]');
      const doppelganger = document.querySelector(".sendbox__text-area-doppelganger");
      if (textarea) textarea.placeholder = "Ask away, just leave out personal information...";
      if (doppelganger) doppelganger.innerText = "Ask away, just leave out personal information...";
    };
    updateSendBoxText();
    const interval = setInterval(updateSendBoxText, 300);
    return () => clearInterval(interval);
  }, []);

  /** Auto-focus the message input once the WebChat connection is established. */
  useEffect(() => {
    if (!connection) return;
    const timer = setTimeout(() => {
      const textarea = document.querySelector('textarea[data-testid="send box text area"]');
      if (textarea) textarea.focus();
    }, 300);
    return () => clearTimeout(timer);
  }, [connection]);

  /** Enforce allowed types + 5 MB limit via DOM interception (file picker). */
  useEffect(() => {
    if (!agentConfig?.allowFileUpload) return;

    const onFileChange = (e) => {
      if (e.target.type !== "file") return;
      const files = Array.from(e.target.files || []);
      if (!files.length) return;
      for (const f of files) {
        if (!ALLOWED_TYPES.includes(f.type)) {
          e.stopPropagation();
          e.stopImmediatePropagation();
          e.target.value = "";
          alert("Unsupported file type. Please upload an image, PDF, or Office document.");
          return;
        }
        if (f.size > MAX_UPLOAD_BYTES) {
          e.stopPropagation();
          e.stopImmediatePropagation();
          e.target.value = "";
          alert(`"${f.name}" exceeds the 5 MB limit (${(f.size / 1024 / 1024).toFixed(2)} MB).`);
          return;
        }
      }
      // Valid — let WebChat stage it; the store subscription moves it to stagedFiles
    };

    // Restrict the OS file picker via accept attribute
    const setAccept = (input) => {
      if (input.accept !== UPLOAD_ACCEPT) input.accept = UPLOAD_ACCEPT;
    };
    document.querySelectorAll('input[type="file"]').forEach(setAccept);
    const observer = new MutationObserver(() => {
      document.querySelectorAll('input[type="file"]').forEach(setAccept);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener("change", onFileChange, true);

    return () => {
      observer.disconnect();
      document.removeEventListener("change", onFileChange, true);
    };
  }, [agentConfig?.allowFileUpload]);

  /**
   * Watch WebChat's sendBoxAttachments Redux state. Whenever WebChat stages a file
   * (via the upload button picker), move it into our own stagedFiles and immediately
   * clear WebChat's staging so its "1 attachment" badge never appears.
   */
  useEffect(() => {
    if (!agentConfig?.allowFileUpload) return;
    const unsubscribe = store.subscribe(() => {
      const webchatAttachments = store.getState().sendBoxAttachments || [];
      if (webchatAttachments.length === 0) return;
      // Move the blob files to our staging area
      const files = webchatAttachments.map((a) => a.blob).filter(Boolean);
      if (files.length > 0) setStagedFiles((prev) => [...prev, ...files]);
      // Clear WebChat's own staging immediately
      store.dispatch({ type: "WEB_CHAT/SET_SEND_BOX_ATTACHMENTS", payload: { attachments: [] } });
    });
    return unsubscribe;
  }, [agentConfig?.allowFileUpload]); // store is stable (useMemo []) — safe to omit from deps

  /**
   * After WebChat mounts, insert a portal-target div just before the .sendbox element.
   * The staging thumbnail strip is rendered into that div via createPortal, so it
   * appears physically above the send box in the DOM regardless of whether the
   * sendbox is sticky (desktop) or fixed (mobile).
   */
  useEffect(() => {
    if (!agentConfig?.allowFileUpload) return;
    if (!connection) return;

    let timeoutId;
    const tryInsert = (attempt = 0) => {
      const sendbox = document.querySelector(".sendbox");
      if (!sendbox?.parentElement) {
        if (attempt < 20) timeoutId = setTimeout(() => tryInsert(attempt + 1), 300);
        return;
      }
      if (stagingPortalRef.current) return; // already inserted

      const portalDiv = document.createElement("div");
      portalDiv.setAttribute("data-staging-strip", "1");
      portalDiv.style.width = "100%";
      portalDiv.style.boxSizing = "border-box";

      // Insert directly before the <textarea> inside its parent so the thumbnail
      // row appears inside the bordered/rounded input container, above where the
      // user types — matching the iOS-style attachment preview pattern.
      const textarea = sendbox.querySelector("textarea")
        || sendbox.querySelector('[contenteditable="true"]');
      const insertParent = textarea ? textarea.parentElement : sendbox;
      const insertBefore = textarea || insertParent.firstChild;
      insertParent.insertBefore(portalDiv, insertBefore);

      stagingPortalRef.current = portalDiv;
      setStagingPortalEl(portalDiv);
    };

    timeoutId = setTimeout(tryInsert, 500);

    return () => {
      clearTimeout(timeoutId);
      if (stagingPortalRef.current) {
        try {
          stagingPortalRef.current.parentElement?.removeChild(stagingPortalRef.current);
        } catch {}
        stagingPortalRef.current = null;
        setStagingPortalEl(null);
      }
    };
  }, [agentConfig?.allowFileUpload, connection]);

  // Insert a portal div between the transcript and the sendbox so we can render
  // the bot-typing loader inside the WebChat layout without touching its DOM directly.
  useEffect(() => {
    if (!connection) return;
    let timeoutId;
    const tryInsertLoader = (attempt = 0) => {
      const sendbox = document.querySelector(".sendbox");
      if (!sendbox?.parentElement) {
        if (attempt < 20) timeoutId = setTimeout(() => tryInsertLoader(attempt + 1), 300);
        return;
      }
      if (loaderPortalRef.current) return;
      const loaderDiv = document.createElement("div");
      loaderDiv.setAttribute("data-typing-loader", "1");
      sendbox.parentElement.insertBefore(loaderDiv, sendbox);
      loaderPortalRef.current = loaderDiv;
      setLoaderPortalEl(loaderDiv);
    };
    tryInsertLoader();
    return () => {
      clearTimeout(timeoutId);
      if (loaderPortalRef.current) {
        try { loaderPortalRef.current.parentElement?.removeChild(loaderPortalRef.current); } catch {}
        loaderPortalRef.current = null;
        setLoaderPortalEl(null);
      }
    };
  }, [connection]);

  useEffect(() => {
    if (isBotTyping) {
      slowTimerRef.current = setTimeout(() => setShowSlowMsg(true), 10000);
    } else {
      clearTimeout(slowTimerRef.current);
      setShowSlowMsg(false);
    }
    return () => clearTimeout(slowTimerRef.current);
  }, [isBotTyping]);

  // Create/revoke object URLs for image previews whenever the staged file list changes.
  useEffect(() => {
    if (!agentConfig?.allowFileUpload) return;
    const urls = stagedFiles.map((f) =>
      f.type.startsWith("image/") ? URL.createObjectURL(f) : null
    );
    setStagedPreviews(urls);
    return () => urls.forEach((u) => u && URL.revokeObjectURL(u));
  }, [stagedFiles, agentConfig?.allowFileUpload]);

  const clientRef = useRef(null);
  const copilotTokenRef = useRef(null);
  const currentConnectionTokenRef = useRef(null);
  const mountedRef = useRef(true);
  const isReinitializingRef = useRef(false);
  const activitySequenceRef = useRef(0);
  const conversationRef = useRef(null);
  const activityStreamRef = useRef(new ReplaySubject());
  const connectionStatusRef = useRef(new BehaviorSubject(0));
  const tokenTimerRef = useRef(null);
  const popupBlockedTimerRef = useRef(null);
  const refreshClientTokenRef = useRef(null);
  const prevAgentKeyRef = useRef(agentKey);

  const { error: msalAuthError } = useMsalAuthentication(interactionType, loginRequest);

  const isDark = theme === "dark";
  const [stagingPortalEl, setStagingPortalEl] = useState(null);
  const stagingPortalRef = useRef(null);

  /** WebChat visual configuration shared across all agents. */
  const styleOptions = {
    accent: "#00809d",
    fontSizeSmall: "80%",
    rootWidth: "100%",
    enableUserContentFeedbackUI: true,
    hideUploadButton: !agentConfig?.allowFileUpload,
    enableDragAndDropFileUploads: false,
    cardEmphasisBackgroundColor: "red",
    botAvatarBackgroundColor: isDark ? "#1f2937" : "#FFFFFF",
    botAvatarInitials: "AI",
    userAvatarInitials: "",
    backgroundColor: isDark ? "#1b2b4b" : "#fff",
    sendBoxTextWrap: true,
    sendBoxButtonColorOnHover: isDark ? "#9ca3af" : "#333",
    hideScrollToEndButton: false,
    feedbackActionsPlacement: "activity-status",
    bubbleBackground: isDark ? "#453573" : "#7f4fd5", //#7f4fd5 || #8e4fff
    bubbleBorderRadius: 5,
    bubbleTextColor: "#fff",
    bubbleFromUserBackground: isDark ? "#890145" : "#d9026f", //#d9026f; || #e60075
    bubbleFromUserBorderRadius: 5,
    bubbleFromUserTextColor: "#fff",
    enableFeedbackUI: true,
    sendTimeout: 60000,
    sendTimeoutForAttachments: 120000,
  };

  /** Clears the proactive token-refresh timer. */
  const clearTokenTimer = () => {
    if (tokenTimerRef.current) {
      clearTimeout(tokenTimerRef.current);
      tokenTimerRef.current = null;
    }
  };

  /**
   * Acquires a Copilot access token silently using the active MSAL account.
   * Throws if no account is available.
   */
  async function getCopilotTokenSilently() {
    const account = instance.getActiveAccount() || accounts[0];
    if (!account) throw new Error("No active MSAL account");
    console.log(new Date().toISOString() + " Silent Token Invoke");
    return await instance.acquireTokenSilent({ account, scopes: COPILOT_SCOPES });
  }

  /**
   * Schedules a proactive token refresh before the current token expires.
   * Retries once after 30 s on failure and updates the Copilot client when a new token arrives.
   */
  const scheduleRefresh = useCallback((acquireResult) => {
    console.log(new Date().toISOString() + "Schedule Refresh - Init");
    clearTokenTimer();
    const expiresOn = acquireResult.expiresOn instanceof Date
      ? acquireResult.expiresOn.getTime()
      : Date.now() + 3600 * 1000;
    const refreshBufferMs = 5 * 60 * 1000;
    const msUntilRefresh = Math.max(5_000, expiresOn - Date.now() - refreshBufferMs);

    tokenTimerRef.current = setTimeout(async () => {
      try {
        const newRes = await getCopilotTokenSilently();
        if (!mountedRef.current || !newRes?.accessToken) return;
        const tokenChanged = newRes.accessToken !== copilotTokenRef.current;
        copilotTokenRef.current = newRes.accessToken;
        if (tokenChanged && currentConnectionTokenRef.current !== newRes.accessToken && !isReinitializingRef.current) {
          await refreshClientTokenRef.current(newRes.accessToken);
        }
        scheduleRefresh(newRes);
      } catch (err) {
        console.error("Scheduled token refresh failed", err);
        if (mountedRef.current) {
          tokenTimerRef.current = setTimeout(async () => {
            try {
              const retryRes = await getCopilotTokenSilently();
              if (!mountedRef.current || !retryRes?.accessToken) return;
              const tokenChanged = retryRes.accessToken !== copilotTokenRef.current;
              copilotTokenRef.current = retryRes.accessToken;
              if (tokenChanged && currentConnectionTokenRef.current !== retryRes.accessToken && !isReinitializingRef.current) {
                await refreshClientTokenRef.current(retryRes.accessToken);
              }
              scheduleRefresh(retryRes);
            } catch (retryErr) {
              console.error("Scheduled token refresh retry failed", retryErr);
            }
          }, 30_000);
        }
      }
    }, msUntilRefresh);
  }, []);

  /**
   * Returns the browser-specific support URL for unblocking popups.
   * Used in the popup-blocked help card shown when MSAL cannot open the auth popup.
   */
  const getAllowPopupsUrl = () => {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes("edg")) return "https://support.microsoft.com/en-us/microsoft-edge/block-pop-ups-in-microsoft-edge-1d8ba4f8-f385-9a0b-e944-aa47339b6bb5";
    if (ua.includes("firefox")) return "https://support.mozilla.org/en-US/kb/pop-blocker-settings-exceptions-troubleshooting";
    if (ua.includes("safari") && !ua.includes("chrome")) return "https://support.apple.com/guide/safari/block-pop-ups-sfri40696/mac";
    return "https://support.google.com/chrome/answer/95472";
  };

  /** Shows the popup-blocked help card after a 3 s delay. Safe to call multiple times. */
  const schedulePopupBlockedMessage = useCallback(() => {
    if (popupBlockedTimerRef.current) return;
    popupBlockedTimerRef.current = setTimeout(() => {
      if (mountedRef.current) setShowPopupBlocked(true);
    }, 3000);
  }, []);

  /** Surfaces the popup-blocked card when useMsalAuthentication itself reports the error. */
  useEffect(() => {
    if (!msalAuthError) return;
    const isPopupBlocked =
      msalAuthError?.errorCode === "popup_window_error" ||
      msalAuthError?.errorCode === "empty_window_error";
    if (isPopupBlocked) schedulePopupBlockedMessage();
  }, [msalAuthError, schedulePopupBlockedMessage]);

  /**
   * Ensures the current Copilot access token has at least minValidMs of lifetime remaining.
   * Forces a silent refresh when the token is about to expire.
   */
  const ensureValidCopilotToken = useCallback(async (minValidMs = 2 * 60 * 1000) => {
    try {
      const result = await instance.acquireTokenSilent({
        account: instance.getActiveAccount(),
        scopes: COPILOT_SCOPES,
      });
      if (!result || !result.accessToken) throw new Error("acquireTokenSilent returned no token");
      const expiresOn = result.expiresOn instanceof Date ? result.expiresOn.getTime() : Date.now() + 3600 * 1000;
      const now = Date.now();
      console.log("expiresOn :: " + expiresOn);
      console.log("now :: " + now);
      console.log("minValidMs :: " + minValidMs);
      if (expiresOn - now < minValidMs) {
        console.log(new Date().toISOString() + "Token is about to expire, hence refresh called");
        try {
          const forced = await instance.acquireTokenSilent({
            account: instance.getActiveAccount(),
            scopes: COPILOT_SCOPES,
            forceRefresh: true,
          });
          if (forced && forced.accessToken) {
            copilotTokenRef.current = forced.accessToken;
            scheduleRefresh(forced);
            return { accessToken: forced.accessToken, expiresOn: forced.expiresOn };
          }
        } catch (forcedErr) {
          console.warn("Forced refresh failed; falling back to original token", forcedErr);
        }
      }
      copilotTokenRef.current = result.accessToken;
      scheduleRefresh(result);
      return { accessToken: result.accessToken, expiresOn: result.expiresOn };
    } catch (err) {
      console.warn("ensureValidCopilotToken error:", err);
      throw err;
    }
  }, []);

  /** Recursively searches an Adaptive Card node tree for an action matching the given title. */
  const findActionByTitle = useCallback((node, title) => {
    if (!node) return null;
    if (Array.isArray(node)) {
      for (const item of node) {
        const match = findActionByTitle(item, title);
        if (match) return match;
      }
      return null;
    }
    if (typeof node !== "object") return null;
    if (typeof node.title === "string" && node.title.trim().toLowerCase() === title) return node;
    for (const value of Object.values(node)) {
      const match = findActionByTitle(value, title);
      if (match) return match;
    }
    return null;
  }, []);

  /** Returns true if the attachment is the Copilot connector consent card (Allow / Cancel). */
  const isAllowCancelCard = useCallback((attachment) => {
    const content = attachment?.content;
    if (!content || attachment?.contentType !== "application/vnd.microsoft.card.adaptive") return false;
    const allowAction = findActionByTitle(content, "allow");
    const cancelAction = findActionByTitle(content, "cancel");
    const bodyText = JSON.stringify(content).toLowerCase();
    return !!allowAction && !!cancelAction && bodyText.includes("connect to continue");
  }, [findActionByTitle]);

  /** Deep-clones an attachment to avoid mutating the original card payload. */
  const cloneAttachment = useCallback((attachment) => {
    try { return JSON.parse(JSON.stringify(attachment)); }
    catch { return attachment; }
  }, []);

  /**
   * Replaces the raw Copilot connector consent card with a simplified user-friendly version
   * that shows only the Allow/Cancel actions and a plain-language permission request.
   */
  const patchAllowCancelCard = useCallback((attachment) => {
    const clonedAttachment = cloneAttachment(attachment);
    const content = clonedAttachment?.content;
    if (!content) return clonedAttachment;
    const allowAction = findActionByTitle(content, "allow");
    const cancelAction = findActionByTitle(content, "cancel");
    const simplifiedBody = [
      { type: "TextBlock", text: "Let's get started....", wrap: true, weight: "Bolder", size: "Medium", color: "Light" },
      { type: "TextBlock", text: "Before I can help, I need your permission to access a few basic profile details, like your country, business unit, and job role, so I can give you the most relevant information.", wrap: true, color: "Light", spacing: "Medium" },
    ];
    if (allowAction) {
      simplifiedBody.push({
        type: "ActionSet",
        spacing: "Medium",
        actions: [
          { ...allowAction, title: "Allow", style: "positive" },
          ...(cancelAction ? [{ ...cancelAction, title: "Cancel" }] : []),
        ],
      });
    }
    content.body = simplifiedBody;
    content.actions = [];
    return clonedAttachment;
  }, [cloneAttachment, findActionByTitle]);

  /** Replaces a consent card with a post-approval confirmation message. */
  const patchApprovedConsentCard = useCallback((attachment) => {
    const clonedAttachment = cloneAttachment(attachment);
    const content = clonedAttachment?.content;
    if (!content) return clonedAttachment;
    content.body = [
      { type: "TextBlock", text: "Thanks for Approving", wrap: true, weight: "Bolder", size: "Large", color: "Light" },
      { type: "TextBlock", text: "Thanks for allowing access. I'm gathering the information you requested...", wrap: true, color: "Light", spacing: "Medium" },
    ];
    content.actions = [];
    return clonedAttachment;
  }, [cloneAttachment]);

  /**
   * Inline component rendered inside the WebChat attachment middleware.
   * Presents Allow / Cancel buttons for Copilot connector consent requests
   * and posts the user's decision back to the bot as a message activity.
   */
  const ConsentDecisionCard = ({ activityId, allowAction }) => {
    const postActivity = usePostActivity();
    const [isResolved, setIsResolved] = useState(false);

    const handleDecision = async (action) => {
      setIsResolved(true);
      setApprovedConsentActivityIds((previous) => {
        if (previous.has(activityId)) return previous;
        const next = new Set(previous);
        next.add(activityId);
        return next;
      });
      await postActivity({
        type: "message",
        text: undefined,
        replyToId: activityId,
        value: action?.data && typeof action.data === "object"
          ? action.data
          : { action: action?.title || "Allow", id: "submit", shouldAwaitUserInput: true },
      });
    };

    if (isResolved) {
      return (
        <div className="bot-action-card__approved">
          <div className="bot-action-card__approved-title">Thanks for Approving</div>
          <div className="bot-action-card__approved-text">
            Thanks for allowing access. I&apos;m gathering the information you requested...
          </div>
        </div>
      );
    }

    return (
      <div className="bot-action-card__approved">
        <div className="bot-action-card__approved-title">Let&apos;s get started....</div>
        <div className="bot-action-card__approved-text">
          Before I can help, I need your permission to access a few basic profile details,
          like your country, business unit, and job role, so I can give you the most relevant information.
        </div>
        <div className="bot-action-card__actions">
          {allowAction ? (
            <button
              type="button"
              className="bot-action-card__button bot-action-card__button--primary"
              onClick={() => handleDecision(allowAction)}
            >
              Allow
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  /**
   * WebChat attachment middleware — intercepts Copilot consent cards and renders
   * ConsentDecisionCard in their place so the user sees a clean permission prompt.
   */
  const attachmentMiddleware = useCallback(
    () => (next) => (card) => {
      const { attachment, activity } = card;

      // Render user-sent attachments: images as a full-width preview, documents as a chip.
      if (activity?.from?.role === "user" && attachment?.name) {
        if (attachment.contentType?.startsWith("image/") && attachment.contentUrl) {
          return (
            <img
              src={attachment.contentUrl}
              alt={attachment.name}
              style={{
                maxWidth: "100%",
                maxHeight: 300,
                borderRadius: 8,
                display: "block",
              }}
            />
          );
        }
        // Non-image file: show icon + filename chip
        return (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "5px 10px", borderRadius: 6,
            background: "rgba(255,255,255,0.15)",
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
              <path d="M21.586 10.461l-10.05 10.075c-1.95 1.949-5.122 1.949-7.071 0s-1.95-5.122 0-7.072l10.628-10.585c1.17-1.17 3.073-1.17 4.243 0 1.169 1.17 1.169 3.072 0 4.242l-8.507 8.464c-.39.39-1.024.39-1.414 0s-.39-1.024 0-1.414l7.093-7.05-1.415-1.415-7.093 7.05c-1.172 1.172-1.172 3.071 0 4.242.586.586 1.354.879 2.121.879s1.535-.293 2.121-.879l8.507-8.464c1.949-1.949 1.949-5.121 0-7.071-1.951-1.951-5.122-1.951-7.072 0l-10.628 10.585c-2.73 2.73-2.73 7.17 0 9.9 2.73 2.73 7.17 2.73 9.9 0l10.05-10.075-1.414-1.414z"/>
            </svg>
            <span style={{ fontSize: "0.8rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "inherit" }}>
              {attachment.name}
            </span>
          </div>
        );
      }

      if (!isAllowCancelCard(card?.attachment)) return next(card);
      const content = attachment?.content;
      const allowAction = findActionByTitle(content, "allow");
      const cancelAction = findActionByTitle(content, "cancel");
      return (
        <div className="bot-action-card bot-action-card--consent">
          <div className="bot-action-card__label">Action required</div>
          <ConsentDecisionCard
            activityId={card?.activity?.id}
            allowAction={allowAction}
            cancelAction={cancelAction}
          />
        </div>
      );
    },
    [findActionByTitle, isAllowCancelCard]
  );

  /**
   * Patches consent card attachments in the activity payload — applies the simplified
   * consent UI before approval and the confirmation card after.
   */
  const patchConsentAttachmentsInCard = useCallback((card) => {
    const attachments = card?.activity?.attachments;
    if (!Array.isArray(attachments) || !attachments.length) return card;
    const activityId = card?.activity?.id;
    const isApprovedConsentCard = !!activityId && approvedConsentActivityIds.has(activityId);
    const updatedAttachments = attachments.map((attachment) => {
      if (!isAllowCancelCard(attachment)) return attachment;
      if (activityId) lastConsentActivityIdRef.current = activityId;
      return isApprovedConsentCard ? patchApprovedConsentCard(attachment) : patchAllowCancelCard(attachment);
    });
    return {
      ...card,
      activity: {
        ...card.activity,
        channelData: {
          ...(card.activity?.channelData || {}),
          feedback: { enabled: !isApprovedConsentCard },
          approvedConsentCard: isApprovedConsentCard,
        },
        attachments: updatedAttachments,
      },
    };
  }, [approvedConsentActivityIds, isAllowCancelCard, patchAllowCancelCard, patchApprovedConsentCard]);

  /**
   * WebChat activity middleware — handles:
   * - Consent card patching
   * - Delete button on user messages
   * - FeedbackBar injection on bot messages
   * - Auto-collapsing the references/citations panel
   */
  const activityMiddleware = () => (next) => (card) => {
    card = patchConsentAttachmentsInCard(card);
    const activity = card.activity;
    const isUser = activity?.from?.role === "user";
    const isDeleted = activity?.id && deletedActivityIds.has(activity.id);

    console.debug("[activityMiddleware] activity incoming:", {
      id: activity?.id,
      from: activity?.from,
      text: activity?.text,
      channelData: activity?.channelData,
      attachmentsCount: (activity?.attachments || []).length,
    });

    if (isDeleted && !isUser) return null;

    if (isUser) {
      return (
        <div className="chat-message-shell">
          {!isDeleted ? (
            <button
              type="button"
              className="message-delete-button"
              aria-label="Delete message"
              title="Delete message"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                const activityElement =
                  event.currentTarget.closest("[data-activity-id]") ||
                  event.currentTarget.closest(".webchat__basic-transcript__activity") ||
                  event.currentTarget.closest(".webchat__stacked-layout__message-row");
                const bubbleContent = activityElement?.querySelector(
                  ".webchat__bubble--from-user .webchat__bubble__content, .webchat__bubble__content"
                );
                if (bubbleContent) {
                  bubbleContent.textContent = "This message has been deleted...";
                  bubbleContent.classList.add("webchat__bubble__content--deleted-message");
                }
                setDeletedActivityIds((previous) => {
                  const next = new Set(previous);
                  next.add(activity.id);
                  return next;
                });
              }}
            >
              <img
                className="message-delete-button__icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
                src="/images/xmark.svg"
                alt="Delete"
              />
            </button>
          ) : null}
          {next(card)}
        </div>
      );
    }

    if (!isUser) {
      try { setTimeout(() => collapseLastReferences?.(), 500); } catch {}

      const patchedActivity = {
        ...activity,
        channelData: {
          ...(activity.channelData || {}),
          feedback: { enabled: true },
          feedbackLoop: activity.channelData?.feedbackLoop || { type: "default" },
        },
      };

      console.debug("[activityMiddleware] patchedActivity:", patchedActivity);

      const patchedCard = { ...card, activity: patchedActivity };
      const showFeedbackBar =
        patchedActivity.channelData?.feedback?.enabled !== false &&
        !patchedActivity.channelData?.approvedConsentCard;
      const hasPendingConsentCard = (patchedActivity.attachments || []).some(
        (attachment) => isAllowCancelCard(attachment)
      );

      const replaceConsentCardInDom = (buttonEl) => {
        const consentCardEl = buttonEl?.closest(".bot-action-card--consent");
        if (!consentCardEl) return;
        consentCardEl.innerHTML = `
          <div class="bot-action-card__approved">
            <div class="bot-action-card__approved-title">Thanks for Approving</div>
            <div class="bot-action-card__approved-text">
              Thanks for allowing access. I'm gathering the information you requested...
            </div>
          </div>
        `;
      };

      const handleConsentDecisionClick = (event) => {
        if (!hasPendingConsentCard || patchedActivity.channelData?.approvedConsentCard) return;
        const clickedButton = event.target.closest("button, [role='button']");
        const actionLabel = clickedButton?.textContent?.trim().toLowerCase();
        if (actionLabel === "allow" || actionLabel === "cancel") {
          replaceConsentCardInDom(clickedButton);
          markConsentCardApprovedById(patchedActivity.id);
        }
      };

      setTimeout(() => {
        const textarea = document.querySelector('textarea[data-testid="send box text area"]');
        if (textarea) textarea.focus();
      }, 30);

      console.log("Activity Middleware - Indicator Block");
      return (
        <div
          style={{ display: "flex", flexDirection: "column" }}
          onClickCapture={handleConsentDecisionClick}
        >
          {next(patchedCard)}
          {showFeedbackBar ? <FeedbackBar activity={patchedActivity} /> : null}
        </div>
      );
    }

    return {};
  };

  /** Collapses the references/citations panel of the most recent bot message. */
  function collapseLastReferences() {
    const messages = document.querySelectorAll(".webchat__link-definitions");
    if (messages.length > 0) {
      messages[messages.length - 1].open = false;
    }
  }

  /** Trims bot response text to the content up to and including the closing </details> tag. */
  const trimBotTextAfterDetails = (text = "") => {
    const detailsEndTag = "</details>";
    if (!text.includes(detailsEndTag)) return text;
    return text.slice(0, text.indexOf(detailsEndTag) + detailsEndTag.length).trim();
  };

  /**
   * WebChat Redux store middleware. Responsibilities:
   * - Trims bot response text after </details> closing tags
   * - Injects feedback channel data on all message activities
   * - Ensures a fresh MSAL token is available before every outgoing user message
   * - Refreshes the Copilot client connection when the token has rotated
   */
  const store = React.useMemo(() => createStore(
    {},
    (storeAPI) => (next) => async (action) => {
      if (action.type === "WEB_CHAT/SEND_FILES" || action.type === "WEB_CHAT/SEND_ATTACHMENT") {
        const files = action.payload?.files || (action.payload?.attachment ? [action.payload.attachment] : []);
        for (const file of files) {
          const mime = file.type || file.contentType || "";
          if (!ALLOWED_TYPES.includes(mime)) {
            alert("Unsupported file type. Please upload an image, PDF, or Office document.");
            return;
          }
          const size = file.size || 0;
          if (size > MAX_UPLOAD_BYTES) {
            alert(`File "${file.name}" exceeds the 5 MB limit (${(size / 1024 / 1024).toFixed(2)} MB).`);
            return;
          }
        }
        return next(action);
      }

      if (action.type === "DIRECT_LINE/INCOMING_ACTIVITY" && action.payload?.activity?.type === "message") {
        console.log("inside Incoming");
        const incoming = action.payload.activity;
        console.log(incoming?.text);
        const isUserEcho = incoming?.from?.role === "user";
        const trimmedIncomingText = isUserEcho
          ? (typeof incoming?.text === "string" ? incoming.text.trim() : "")
          : trimBotTextAfterDetails(incoming?.text || "");
        console.log("trimmedIncomingText :: ");
        console.log(trimmedIncomingText);
        const result = next({
          ...action,
          payload: {
            ...action.payload,
            activity: {
              ...incoming,
              text: trimmedIncomingText,
              channelData: {
                ...(incoming.channelData || {}),
                feedback: { enabled: true },
                feedbackLoop: incoming.channelData?.feedbackLoop || { type: "default" },
              },
            },
          },
        });
        // Hide loading animation once any message lands (bot response clears it).
        if (!isUserEcho) setBotTypingRef.current?.(false);
        // Scroll to bottom after the new message is rendered in the DOM.
        setTimeout(() => onSendboxClickRef.current?.(), 100);
        return result;
      }

      if (!(action.type === "DIRECT_LINE/POST_ACTIVITY" && action.payload?.activity?.type === "message")) {
        console.log("inside Not post");
        return next(action);
      }

      const activity = action.payload.activity;
      if (msalUser == null) {
        msalUser = window.__MSAL_USER__;
      }

      let cleanActivity = null;
      if (activity.text == null || activity.text === "") {
        cleanActivity = {
          ...activity,
          text: activity.text,
          value: activity.value && typeof activity.value === "object"
            ? activity.value
            : { action: "Allow", id: "submit", shouldAwaitUserInput: true },
          channelData: {
            ...(activity.channelData || {}),
            feedback: { enabled: true },
            feedbackLoop: activity.channelData?.feedbackLoop || { type: "default" },
          },
        };
      } else {
        cleanActivity = {
          ...activity,
          text: activity.text,
          value: {},
          channelData: {
            ...(activity.channelData || {}),
            feedback: { enabled: true },
            feedbackLoop: activity.channelData?.feedbackLoop || { type: "default" },
          },
        };
      }

      let tokenInfo = null;
      try {
        console.log(new Date().toISOString() + "Ensure Token validity");
        tokenInfo = await ensureValidCopilotToken(2 * 60 * 1000);
        if (tokenInfo && tokenInfo.accessToken) copilotTokenRef.current = tokenInfo.accessToken;
      } catch (err) {
        const isInteractionRequired =
          err instanceof InteractionRequiredAuthError ||
          err?.errorCode === "interaction_required" ||
          err?.errorCode === "consent_required";
        if (isInteractionRequired) {
          try {
            const interactiveResult = await getCopilotTokenSilently();
            console.log(new Date().toISOString() + " - Is Interaction Required");
            if (interactiveResult && interactiveResult.accessToken) {
              copilotTokenRef.current = interactiveResult.accessToken;
              scheduleRefresh(interactiveResult);
            } else {
              console.warn("Interactive token acquire returned no token.");
            }
          } catch (interactiveErr) {
            console.error("Interactive token acquisition failed:", interactiveErr);
          }
        } else {
          console.warn("Silent token check failed; proceeding to send (may 401):", err);
        }
      }

      try {
        if (copilotTokenRef.current && currentConnectionTokenRef.current !== copilotTokenRef.current) {
          if (!isReinitializingRef.current) {
            await refreshClientTokenRef.current(copilotTokenRef.current);
          } else {
            console.log("Reinit already in progress; posting action while reinit completes.");
          }
        }
      } catch (reinitErr) {
        console.warn("Failed to recreate connection before posting activity:", reinitErr);
      }

      // Convert any staged files to base64 inline attachments so they appear
      // inside the same user message bubble rather than as a separate message.
      const filesToSend = stagedFilesRef.current;
      let fileAttachments = [];
      if (filesToSend.length > 0) {
        stagedFilesSetterRef.current([]);
        try {
          fileAttachments = await Promise.all(
            filesToSend.map(
              (file) =>
                new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = (e) =>
                    resolve({ contentType: file.type, contentUrl: e.target.result, name: file.name });
                  reader.onerror = reject;
                  reader.readAsDataURL(file);
                })
            )
          );
        } catch (err) {
          console.warn("Failed to convert staged files to attachments:", err);
        }
      }

      // Merge file attachments into the activity. Text stays clean; the image/file
      // is rendered by attachmentMiddleware directly in the chat bubble.
      const activityToDispatch = fileAttachments.length > 0
        ? {
            ...cleanActivity,
            attachments: [...(cleanActivity.attachments || []), ...fileAttachments],
          }
        : cleanActivity;

      const dispatched = next({
        ...action,
        payload: { ...action.payload, activity: activityToDispatch },
      });
      setBotTypingRef.current?.(true);

      setTimeout(async () => {
        try {
          if (copilotTokenRef.current && currentConnectionTokenRef.current !== copilotTokenRef.current) {
            if (!isReinitializingRef.current) await refreshClientTokenRef.current(copilotTokenRef.current);
          }
        } catch (resendErr) {
          console.warn("Post-send token refresh failed:", resendErr);
        }
      }, 1200);

      return dispatched;
    }
  ), []);

  window.webChatStore = store;

  /** Emits an activity into the WebChat activity stream, maintaining conversation context. */
  const emitActivity = useCallback((activity) => {
    if (!activity) return;
    const conversation = activity?.conversation?.id ? activity.conversation : conversationRef.current;
    if (conversation?.id) conversationRef.current = conversation;
    activityStreamRef.current.next({
      ...activity,
      conversation,
      timestamp: activity.timestamp || new Date().toISOString(),
      channelData: {
        ...(activity?.channelData || {}),
        "webchat:sequence-id": activitySequenceRef.current++,
      },
    });
  }, []);

  /** Returns true when the activity is a consent allow/cancel decision (no text, action value). */
  const isConsentDecisionActivity = useCallback(
    (activity) =>
      activity?.type === "message" &&
      !activity?.text &&
      ["allow", "cancel"].includes(String(activity?.value?.action || "").trim().toLowerCase()),
    []
  );

  /** Marks a consent card approved via the activity that triggered the consent prompt. */
  const markConsentCardApproved = useCallback((activity) => {
    const consentActivityId = activity?.replyToId || lastConsentActivityIdRef.current;
    if (!consentActivityId) return;
    setApprovedConsentActivityIds((previous) => {
      if (previous.has(consentActivityId)) return previous;
      const next = new Set(previous);
      next.add(consentActivityId);
      return next;
    });
  }, []);

  /** Marks a consent card approved by its direct activity ID. */
  const markConsentCardApprovedById = useCallback((consentActivityId) => {
    if (!consentActivityId) return;
    setApprovedConsentActivityIds((previous) => {
      if (previous.has(consentActivityId)) return previous;
      const next = new Set(previous);
      next.add(consentActivityId);
      return next;
    });
  }, []);

  /**
   * Creates the stable DirectLine-compatible connection object passed to the WebChat Composer.
   * Bridges WebChat's postActivity calls to the Copilot Studio SDK client.
   */
  const createStableConnection = useCallback(() => ({
    connectionStatus$: connectionStatusRef.current,
    activity$: activityStreamRef.current.asObservable(),
    postActivity(activity) {
      return new Observable((subscriber) => {
        (async () => {
          try {
            const conversationId = activity?.conversation?.id || conversationRef.current?.id;
            const userActivity = {
              ...activity,
              id: activity?.id || `local-${Date.now()}-${activitySequenceRef.current}`,
              conversation: conversationId
                ? { ...(conversationRef.current || {}), id: conversationId }
                : activity?.conversation,
            };
            emitActivity(userActivity);
            if (isConsentDecisionActivity(userActivity)) markConsentCardApproved(userActivity);
            emitActivity({
              type: "typing",
              from: conversationRef.current
                ? { id: conversationRef.current.id, name: conversationRef.current.name }
                : { id: "agent", name: "Agent" },
              conversation: conversationRef.current,
            });
            const client = clientRef.current;
            if (!client) throw new Error("Copilot client is not initialized.");
            const activities = await client.sendActivity(userActivity, conversationId);
            activities.forEach((incomingActivity) => emitActivity(incomingActivity));
            subscriber.next(userActivity.id);
            subscriber.complete();
          } catch (error) {
            subscriber.error(error);
          }
        })();
      });
    },
    end() {
      connectionStatusRef.current.next(5);
      activityStreamRef.current.complete();
    },
  }), [emitActivity, isConsentDecisionActivity, markConsentCardApproved]);

  /**
   * WebChat avatar middleware — renders the signed-in user's Microsoft profile photo
   * (falling back to a default image) and the agent's bot avatar from agentConfig.
   */
  const invertBotAvatar = isDark && agentConfig.botAvatar &&
    (agentConfig.botAvatar.includes("itsd") || agentConfig.botAvatar.includes("ts-icon"));

  const avatarMiddleware =
    () =>
    (next) =>
    ({ fromUser }) => {
      const userPicSrc = typeof window !== "undefined" ? window.__MSAL_USER_PIC__ : null;
      const avatarSrc = fromUser
        ? (userPicSrc && userPicSrc !== "" ? userPicSrc : "/images/defaultuser.png")
        : agentConfig.botAvatar;
      const imgStyle = {
        width: "100%",
        height: "100%",
        ...(!fromUser && invertBotAvatar ? { filter: "brightness(0) invert(1)" } : {}),
      };
      return (
        <div style={{ width: 40, height: 40, borderRadius: "50%", overflow: "hidden", marginRight: fromUser ? 0 : 10, marginLeft: fromUser ? 10 : 0 }}>
          <img src={avatarSrc} alt="avatar" style={imgStyle} />
        </div>
      );
    };

  /**
   * Injects programmatic text into the WebChat sendbox and auto-submits it.
   * Used by the quick-question shortcut buttons rendered above the chat.
   */
  const setTextboxValue = async (text) => {
    const textbox = document.querySelector('textarea[aria-label="Message input box"]');
    textbox.value = text;
    await textbox.dispatchEvent(new Event("input", { bubbles: true }));
    sendTextAuto();
  };

  /** Programmatically clicks the WebChat send button after setTextboxValue populates it. */
  const sendTextAuto = async () => {
    const button = document.querySelector('button[aria-label="Send"]');
    button.click();
    await button.dispatchEvent(new Event("click"));
  };

  /**
   * Re-instantiates the CopilotStudioClient with a new access token.
   * Guards against concurrent re-initializations via isReinitializingRef.
   */
  const refreshClientToken = async (token) => {
    console.log(new Date().toISOString() + " - Inside Refresh Client Token");
    if (!token) { console.warn("refreshClientToken called without token"); return; }
    if (currentConnectionTokenRef.current === token) {
      console.log(new Date().toISOString() + " - current Tokenref return");
      return;
    }
    if (isReinitializingRef.current) {
      console.log("Connection reinit already in progress — skipping duplicate reinit");
      return;
    }
    isReinitializingRef.current = true;
    try {
      clientRef.current = new CopilotStudioClient(agentConfig, token);
      currentConnectionTokenRef.current = token;
      console.log("Copilot client refreshed. tokenLen:", token?.length || 0);
    } catch (err) {
      console.error("Failed to recreate Copilot connection:", err);
    } finally {
      setTimeout(() => { isReinitializingRef.current = false; }, 300);
    }
  };
  refreshClientTokenRef.current = refreshClientToken;

  /**
   * Establishes the initial WebChat DirectLine connection and starts the Copilot conversation.
   * Creates the stable connection object, signals ready status, and emits the opening activity.
   */
  const initializeConnection = useCallback(async (token) => {
    await refreshClientToken(token);
    if (!connection) {
      const stableConnection = createStableConnection();
      setConnection(stableConnection);
    } else {
      connectionStatusRef.current.next(2);
      return;
    }
    const startActivity = await clientRef.current.startConversationAsync();
    if (startActivity?.conversation?.id) conversationRef.current = startActivity.conversation;
    connectionStatusRef.current.next(2);
    emitActivity(startActivity);
  }, [createStableConnection, emitActivity, connection]);

  /**
   * Bootstraps the Copilot connection on mount.
   * Waits for MSAL auth to settle, acquires the initial token, and starts the conversation.
   * Cleans up timers on unmount.
   */
  useEffect(() => {
    mountedRef.current = true;
    const cleanup = () => {
      mountedRef.current = false;
      clearTokenTimer();
      if (popupBlockedTimerRef.current) {
        clearTimeout(popupBlockedTimerRef.current);
        popupBlockedTimerRef.current = null;
      }
      // Connection teardown (next(5) / complete) is intentionally NOT here.
      // This cleanup fires on every dep change, not just unmount. Signalling
      // ChannelEnded here would kill the live connection each time `connection`
      // state updates (which causes initializeConnection to recreate, which
      // changes this effect's deps). See the [] effect below for true unmount.
    };

    if (prevAgentKeyRef.current !== agentKey) {
      prevAgentKeyRef.current = agentKey;
      clearTokenTimer();
      connectionStatusRef.current.next(5);
      try { activityStreamRef.current.complete(); } catch {}
      activityStreamRef.current = new ReplaySubject();
      connectionStatusRef.current = new BehaviorSubject(0);
      activitySequenceRef.current = 0;
      clientRef.current = null;
      conversationRef.current = null;
      currentConnectionTokenRef.current = null;
      initializedRef.current = false;
      setConnection(null);
      return cleanup;
    }

    if (inProgress !== "none") return cleanup;
    if (accounts.length === 0) return cleanup;
    if (initializedRef.current) return cleanup;
    initializedRef.current = true;
    (async () => {
      try {
        const result = await getCopilotTokenSilently();
        if (!mountedRef.current) return;
        if (!result?.accessToken) throw new Error("Could not obtain Copilot token");
        copilotTokenRef.current = result.accessToken;
        await initializeConnection(result.accessToken);
        if (!mountedRef.current) return;
        scheduleRefresh(result);
      } catch (err) {
        console.error("Initial token / Copilot initialization failed", err);
        initializedRef.current = false;
        const isPopupBlocked =
          err?.errorCode === "popup_window_error" ||
          err?.errorCode === "empty_window_error";
        if (isPopupBlocked) schedulePopupBlockedMessage();
      }
    })();
    return cleanup;
  }, [accounts.length, inProgress, instance, initializeConnection, agentKey]);

  // Owns the lifecycle of the RxJS subjects.
  //
  // React StrictMode (development) double-invokes effects: setup → cleanup → setup.
  // The cleanup of the first pass calls next(5)/complete(), permanently killing those
  // subjects before any async initialization has a chance to use them.
  // By re-assigning fresh subjects on *every* setup, the second pass (and the async
  // IIFE from the first pass, which checks mountedRef before proceeding) always
  // operates on live, uncompleted subjects.
  //
  // The [] dep array still guarantees the cleanup fires only on true unmount
  // (not on bootstrap-effect dep-change re-runs).
  useEffect(() => {
    activityStreamRef.current = new ReplaySubject();
    connectionStatusRef.current = new BehaviorSubject(0);
    return () => {
      connectionStatusRef.current.next(5);
      try { activityStreamRef.current.complete(); } catch {}
    };
  }, []);

  /**
   * FeedbackBar — inline thumbs-up / thumbs-down rating widget.
   * Fires a 'messageFeedback' event back to the bot after a vote is cast.
   * Renders a thank-you message once a vote is recorded.
   */
  const FeedbackBar = ({ activity }) => {
    const postActivity = usePostActivity();
    const [vote, setVote] = useState(null);
    const disabled = !!vote;

    const handleVote = async (direction) => {
      if (vote === direction) return;
      setVote(direction);
      await postActivity({
        type: "event",
        name: "messageFeedback",
        value: { activityId: activity?.id, vote: direction, source: "webchat" },
      });
    };

    return (
      <div>
        {(vote == null || !vote) && (
          <div className="ratingblock">
            <span className="likename">Rate this response</span>
            <svg
              className="thumb up"
              width="20px"
              height="20px"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => handleVote("up")}
              disabled={disabled}
              style={{ cursor: disabled ? "not-allowed" : "pointer" }}
            >
              <path
                d="M20.22 9.55C19.79 9.04 19.17 8.75 18.5 8.75H14.47V6C14.47 4.48 13.24 3.25 11.64 3.25C10.94 3.25 10.31 3.67 10.03 4.32L7.49 10.25H5.62C4.31 10.25 3.25 11.31 3.25 12.62V18.39C3.25 19.69 4.32 20.75 5.62 20.75H17.18C18.27 20.75 19.2 19.97 19.39 18.89L20.71 11.39C20.82 10.73 20.64 10.06 20.21 9.55H20.22ZM5.62 19.25C5.14 19.25 4.75 18.86 4.75 18.39V12.62C4.75 12.14 5.14 11.75 5.62 11.75H7.23V19.25H5.62ZM17.92 18.63C17.86 18.99 17.55 19.25 17.18 19.25H8.74V11.15L11.41 4.9C11.45 4.81 11.54 4.74 11.73 4.74C12.42 4.74 12.97 5.3 12.97 5.99V10.24H18.5C18.73 10.24 18.93 10.33 19.07 10.5C19.21 10.67 19.27 10.89 19.23 11.12L17.91 18.62L17.92 18.63Z"
                fill="#fff"
                style={{ fill: vote === "up" ? "#4caf50" : "#696868" }}
              />
            </svg>
            <svg
              className="thumb down"
              width="20px"
              height="20px"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => handleVote("down")}
              disabled={disabled}
              style={{ cursor: disabled ? "not-allowed" : "pointer" }}
            >
              <path
                d="M18.38 3.25H6.81C5.72 3.25 4.79 4.03 4.6 5.11L3.29 12.61C3.18 13.27 3.36 13.94 3.78 14.45C4.21 14.96 4.83 15.25 5.5 15.25H9.53V18C9.53 19.52 10.76 20.75 12.36 20.75C13.06 20.75 13.69 20.33 13.97 19.68L16.51 13.75H18.39C19.7 13.75 20.76 12.69 20.76 11.38V5.61C20.76 4.31 19.7 3.25 18.39 3.25H18.38ZM15.26 12.85L12.59 19.1C12.55 19.19 12.46 19.26 12.27 19.26C11.58 19.26 11.03 18.7 11.03 18.01V13.76H5.5C5.27 13.76 5.07 13.67 4.93 13.5C4.78 13.33 4.73 13.11 4.77 12.88L6.08 5.38C6.14 5.02 6.45001 4.76 6.82 4.76H15.26V12.85ZM19.25 11.38C19.25 11.86 18.86 12.25 18.38 12.25H16.77V4.75H18.38C18.86 4.75 19.25 5.14 19.25 5.61V11.38Z"
                fill="#fff"
                style={{ fill: vote === "down" ? "#f44336" : "#696868" }}
              />
            </svg>
          </div>
        )}
        {vote && <div className="thanksfeedback">Thanks for your feedback!</div>}
      </div>
    );
  };

  if (!connection) {
    if (showPopupBlocked) {
      return (
        <div className="popup-blocked-overlay">
          <div className="popup-blocked-card">
            <div className="popup-blocked-card__badges">
              <span className="popup-blocked-card__badge popup-blocked-card__badge--warn" aria-hidden="true">!</span>
              <button
                type="button"
                className="popup-blocked-card__badge popup-blocked-card__badge--close"
                aria-label="Dismiss"
                onClick={() => setShowPopupBlocked(false)}
              >
                ✕
              </button>
            </div>
            <h3 className="popup-blocked-card__title">Security Popup Blocked</h3>
            <p className="popup-blocked-card__body">
              It looks like popups are blocked in your browser. Please allow popups for this site to continue.
            </p>
            <a
              className="popup-blocked-card__link"
              href={getAllowPopupsUrl()}
              target="_blank"
              rel="noopener noreferrer"
            >
              Show me how to allow popups
            </a>
          </div>
        </div>
      );
    }
    return (
      <div className="text-center">
        <img src="/images/lnw-loader.gif" alt="Loading.." />
        <h2 className="text-center">Loading Chat ..!</h2>
      </div>
    );
  }

  return (
    <div className="popupWindow">
      {showPopupBlocked && (
        <div className="popup-blocked-overlay">
          <div className="popup-blocked-card">
            <div className="popup-blocked-card__badges">
              <span className="popup-blocked-card__badge popup-blocked-card__badge--warn" aria-hidden="true">!</span>
              <button
                type="button"
                className="popup-blocked-card__badge popup-blocked-card__badge--close"
                aria-label="Dismiss"
                onClick={() => setShowPopupBlocked(false)}
              >
                ✕
              </button>
            </div>
            <h3 className="popup-blocked-card__title">Security Popup Blocked</h3>
            <p className="popup-blocked-card__body">
              It looks like popups are blocked in your browser. Please allow popups for this site to continue.
            </p>
            <a
              className="popup-blocked-card__link"
              href={getAllowPopupsUrl()}
              target="_blank"
              rel="noopener noreferrer"
            >
              Show me how to allow popups
            </a>
          </div>
        </div>
      )}

      <div className="popupHeader">
        <p className="p-questions-class">Common Questions:{" "}</p>
        <div className="popular-queries">
          {agentConfig.quickQuestions.map((q, i) => (
            <button key={i} className="question-button" onClick={() => setTextboxValue(q.text)}>
              {q.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className="webchat-wrapper"
        onDragOver={agentConfig?.allowFileUpload ? (e) => e.preventDefault() : undefined}
        onDrop={agentConfig?.allowFileUpload ? (e) => {
          e.preventDefault();
          const files = Array.from(e.dataTransfer?.files || []);
          for (const f of files) {
            if (!ALLOWED_TYPES.includes(f.type)) { alert("Unsupported file type. Please upload an image, PDF, or Office document."); return; }
            if (f.size > MAX_UPLOAD_BYTES) { alert(`"${f.name}" exceeds the 5 MB limit (${(f.size / 1024 / 1024).toFixed(2)} MB).`); return; }
          }
          setStagedFiles((prev) => [...prev, ...files]);
        } : undefined}
      >
        <FluentThemeProvider>
          <Composer
            directLine={connection}
            store={store}
            activityStatusMiddleware={activityMiddleware}
            attachmentMiddleware={attachmentMiddleware}
            avatarMiddleware={avatarMiddleware}
            styleOptions={styleOptions}
          >
            <BasicWebChat />
          </Composer>
        </FluentThemeProvider>
      </div>

      {/* Attachment preview row — inserted before the <textarea> inside the input
          container, matching the iOS-style pattern: thumbnail chip above the text. */}
      {stagingPortalEl && stagedFiles.length > 0 && createPortal(
        <div style={{
          padding: "6px 8px 2px",
          display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center",
        }}>
          {stagedFiles.map((f, i) => {
            const isImage = f.type.startsWith("image/");
            const previewUrl = stagedPreviews[i];
            return (
              /* Thumbnail with × overlaid in the top-right corner */
              <div key={i} style={{ position: "relative", display: "inline-block", flexShrink: 0 }}>
                {isImage && previewUrl ? (
                  <img
                    src={previewUrl}
                    alt={f.name}
                    style={{
                      width: 64, height: 64, display: "block",
                      objectFit: "cover", borderRadius: 8,
                      border: `1px solid ${isDark ? "#4a5568" : "#d1d5db"}`,
                    }}
                  />
                ) : (
                  <div style={{
                    width: 64, height: 64, borderRadius: 8,
                    background: isDark ? "#1e3a5f" : "#e2e8f0",
                    border: `1px solid ${isDark ? "#4a5568" : "#d1d5db"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"
                      style={{ opacity: 0.55, color: isDark ? "#94a3b8" : "#475569" }}>
                      <path d="M21.586 10.461l-10.05 10.075c-1.95 1.949-5.122 1.949-7.071 0s-1.95-5.122 0-7.072l10.628-10.585c1.17-1.17 3.073-1.17 4.243 0 1.169 1.17 1.169 3.072 0 4.242l-8.507 8.464c-.39.39-1.024.39-1.414 0s-.39-1.024 0-1.414l7.093-7.05-1.415-1.415-7.093 7.05c-1.172 1.172-1.172 3.071 0 4.242.586.586 1.354.879 2.121.879s1.535-.293 2.121-.879l8.507-8.464c1.949-1.949 1.949-5.121 0-7.071-1.951-1.951-5.122-1.951-7.072 0l-10.628 10.585c-2.73 2.73-2.73 7.17 0 9.9 2.73 2.73 7.17 2.73 9.9 0l10.05-10.075-1.414-1.414z"/>
                    </svg>
                  </div>
                )}
                {/* × overlaid inside the top-right corner of the thumbnail */}
                <button
                  type="button"
                  title="Remove"
                  onClick={() => setStagedFiles((prev) => prev.filter((_, j) => j !== i))}
                  style={{
                    position: "absolute", top: 4, right: 4,
                    width: 18, height: 18,
                    background: "rgba(0,0,0,0.55)", color: "#fff",
                    border: "none", borderRadius: "50%",
                    padding: 0, cursor: "pointer",
                    fontSize: "0.6rem", lineHeight: 1,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >✕</button>
              </div>
            );
          })}
        </div>,
        stagingPortalEl
      )}

      {loaderPortalEl && createPortal(
        <div className={isBotTyping ? "bot-typing-indicator" : "bot-typing-indicator bot-typing-indicator--hidden"}>
          <div className="webchat__typing-indicator" />
          {showSlowMsg && (
            <span className="bot-slow-msg">I&apos;m researching that now. Please bear with me...</span>
          )}
        </div>,
        loaderPortalEl
      )}

      <p className="infomsg">Remember, I&apos;m AI, so always double-check my responses. v{settings.version}</p>
    </div>
  );
}

export default CopilotChat;
